/**
 * Detects the native mobile shell (an Expo WebView wrapper that injects
 * `window.CourtVibeNative`) and exposes async helpers that prefer the
 * native share sheet, system clipboard, and OS calendar when present.
 *
 * On a regular browser these helpers report "no bridge" so callers can
 * fall back to the existing browser code paths (`navigator.share`,
 * `navigator.clipboard`, `.ics` download) unchanged.
 */

export interface NativeShareInput {
  title?: string;
  text?: string;
  url?: string;
}

export interface NativeShareResult {
  ok: boolean;
  cancelled?: boolean;
  error?: string;
}

export interface NativeCopyResult {
  ok: boolean;
  error?: string;
}

export interface NativeCalendarEvent {
  title: string;
  description?: string;
  /** ISO 8601 start time (UTC or with timezone offset). */
  startISO: string;
  /** ISO 8601 end time. */
  endISO: string;
  location?: string;
}

export type NativePermission = "granted" | "denied" | "undetermined";

export interface NativeCalendarResult {
  ok: boolean;
  cancelled?: boolean;
  permission?: NativePermission;
  error?: string;
}

export interface CourtVibeNativeBridge {
  isNative?: boolean;
  platform?: string;
  /**
   * Set by the native shell when the OS-level notification permission
   * is denied while the in-app preference is still on. Web code can
   * read this synchronously and listen for `courtvibe:push-blocked-changed`
   * on `window` to react to changes.
   */
  pushBlocked?: boolean;
  /** Push-related bridge methods installed by the mobile shell. */
  notifyPushEnabled?: () => void;
  notifyPushDisabled?: () => void;
  notifySignedOut?: () => void;
  requestPushUnregister?: () => void;
  /** Asks the native shell to open the OS settings page for this app. */
  openOSSettings?: () => void;
  /** Present once the share/copy/calendar bridge is wired up. */
  share?: (payload: NativeShareInput) => Promise<NativeShareResult>;
  copy?: (text: string) => Promise<NativeCopyResult>;
  addToCalendar?: (event: NativeCalendarEvent) => Promise<NativeCalendarResult>;
}

declare global {
  interface Window {
    CourtVibeNative?: CourtVibeNativeBridge;
  }
}

export function getNativeBridge(): CourtVibeNativeBridge | null {
  if (typeof window === "undefined") return null;
  return window.CourtVibeNative ?? null;
}

function getBridge(): CourtVibeNativeBridge | undefined {
  return getNativeBridge() ?? undefined;
}

export function isInNativeShell(): boolean {
  return getNativeBridge()?.isNative === true;
}

export function hasNativeShare(): boolean {
  return typeof getBridge()?.share === "function";
}

export function hasNativeCopy(): boolean {
  return typeof getBridge()?.copy === "function";
}

export function hasNativeCalendar(): boolean {
  return typeof getBridge()?.addToCalendar === "function";
}

/** Returns null when the bridge is unavailable; the caller should then
 * fall back to the browser path. */
export async function nativeShare(
  payload: NativeShareInput,
): Promise<NativeShareResult | null> {
  const fn = getBridge()?.share;
  if (!fn) return null;
  try {
    return await fn(payload);
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export async function nativeCopy(text: string): Promise<NativeCopyResult | null> {
  const fn = getBridge()?.copy;
  if (!fn) return null;
  try {
    return await fn(text);
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export async function nativeAddToCalendar(
  event: NativeCalendarEvent,
): Promise<NativeCalendarResult | null> {
  const fn = getBridge()?.addToCalendar;
  if (!fn) return null;
  try {
    return await fn(event);
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}
